import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CashbackFacade } from '@features/cashback/facades/cashback.facade';
import { EnrichedCashbackFormData } from '@features/cashback/types';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { MembershipFacade } from '@features/membership/facades/membership.facade';
import { PromocodeFacade } from '@features/promocode/facades/promocode.facade';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { FeatureFlagService } from '@rd/core/services/feature-flag/feature-flag.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { CheckoutErrorDialogComponent } from '@rd/shared-legacy/components/checkout-error-dialog/checkout-error-dialog.component';
import {
  CashBackMembership,
  HandleApplyPromocodeData,
  HttpError,
  LoyaltyProgramCategory,
  SubmitCashRedemptionData,
} from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { RedeemCashV2State } from '../../store/reducers';

@UntilDestroy()
@Component({
  selector: 'rd-rcv2-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RcV2ReviewComponent implements OnInit, OnDestroy {
  @HostBinding('class.rd-rcv2-review') componentClass = true;

  promocode$ = this.promocodeFacade.promocode$;
  promocodeUsage$ = this.promocodeFacade.promocodeUsage$;
  promocodeError$ = this.promocodeFacade.promocodeError$;
  promocodeLoading$ = this.promocodeFacade.promocodeLoading$;

  // Can only have either cashback to card to cashback to account
  redeemCashMembership$ = this.membershipFacade.preferredCashBackMembership$;

  redeemCashLoyaltyProgram$ = this.redeemCashMembership$.pipe(
    filter(isNotNull), // A user needs to have a membership to get to review page
    switchMap((membership: CashBackMembership) => {
      switch (membership.category) {
        case LoyaltyProgramCategory.CashbackToCard: {
          return this.loyaltyProgramFacade.cashBackToCardLoyaltyProgram$;
        }
        case LoyaltyProgramCategory.CashbackToAccount: {
          return this.loyaltyProgramFacade.cashBackToAccountLoyaltyProgram$;
        }
        case LoyaltyProgramCategory.CashbackToPlatform: {
          return this.loyaltyProgramFacade.cashBackToPlatformLoyaltyProgram$;
        }
      }
    }),
  );

  checkoutCashRedemptionLoading$ = combineLatest([
    this.cashBackFacade.cashbackLoading$,
    this.membershipFacade.loadMembershipsLoading$,
  ]).pipe(
    map((loadingStates) => loadingStates.some((loadingState) => loadingState)),
  );

  checkoutFormData$ = combineLatest([
    this.cashBackFacade.cashbackForm$.pipe(filter(isNotNull)),
    this.promocodeUsage$,
  ]).pipe(
    map(([formData, promodeUsage]) => {
      const enrichedFormData: EnrichedCashbackFormData = {
        sourceAmount: formData!.sourceAmount,
        targetAmount: formData!.targetAmount,
        sourceAmountPaid: promodeUsage?.pointsPaid,
      };
      return enrichedFormData;
    }),
  );

  backUrl: string = this.featureFlagService.isAutoRedemptionEnabled
    ? '/redeem_cash/checkout/enrollment'
    : '/redeem_cash/checkout';

  constructor(
    private store: Store<{ redeemCash: RedeemCashV2State }>,
    private dialogService: DialogService,
    private analyticsService: AnalyticsService,
    private pageTitleService: PageTitleService,
    private membershipFacade: MembershipFacade,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
    private promocodeFacade: PromocodeFacade,
    private featureFlagService: FeatureFlagService,
    private cashBackFacade: CashbackFacade,
  ) {}

  ngOnInit(): void {
    this.waitForError();
    this.pageTitleService.setPageTitle('redeem-cash-review');
    this.analyticsService.track({
      // @ts-ignore
      routeName: this.analyticsService.sitemap.redeemCash.review,
    });

    this.checkoutFormData$
      .pipe(first((formData) => !!formData))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.analyticsService.track({
          // @ts-ignore
          routeName: this.analyticsService.sitemap.redeemCash.reviewFullData,
        });
      });
  }

  submitCashRedemption({
    loyaltyProgramId,
    formData,
    promocode,
    promocodeUsage,
  }: SubmitCashRedemptionData): void {
    this.cashBackFacade.submitCashback({
      loyaltyProgramId,
      formData,
      promocode,
      promocodeUsage,
    });
  }

  handleApplyPromocode({
    loyaltyProgramId,
    formData,
    promocode,
  }: HandleApplyPromocodeData) {
    if (promocode) {
      this.promocodeFacade.validatePromocode({
        promo_code: {
          code: promocode,
        },
        cash_redemption: {
          loyalty_program_id: loyaltyProgramId,
          redemption_amount: formData.sourceAmount,
          cash_amount: formData.targetAmount,
        },
      });
    } else {
      this.promocodeFacade.clearPromocodeData();
    }
  }

  ngOnDestroy(): void {
    // Remove promocode since promocode for RC is non-persistent
    // We call this in ngOnDestroy instead of ngOnInit
    // cause there is a case when we redirect from MFA if we remove it in ngOnInit
    // we will lose the promocode status here.
    this.promocodeFacade.clearPromocodeData();
  }

  private waitForError(): void {
    this.cashBackFacade.cashbackError$
      .pipe(untilDestroyed(this))
      .subscribe((error) => {
        if (error !== null) {
          this.openErrorDialog(error);
        }
      });
  }

  private openErrorDialog(error: HttpError): void {
    this.dialogService
      .open(CheckoutErrorDialogComponent, {
        data: { error, iconName: 'unexpected-error' },
        id: 'rc-checkout-error-dialog',
        screenMode: 'center-md',
      })
      .afterClosed.subscribe(() => {
        this.cashBackFacade.clearCashbackError();
      });
  }
}
